import { useMemo, useState } from "react";
import type { FormEvent } from "react";

import { createApiClient } from "../infrastructure/http/apiClient";
import { getBillingCapabilities, type BillingCapabilities } from "../modules/billing";

type DiagnosticState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; capabilities: BillingCapabilities }
  | { status: "error"; title: string; message: string; retryable: boolean };

export function CapabilitiesDiagnosticPage() {
  const [token, setToken] = useState("");
  const [state, setState] = useState<DiagnosticState>({ status: "idle" });

  const apiClient = useMemo(() => createApiClient({
    getAuthToken: () => token.trim() || undefined
  }), [token]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!token.trim()) {
      setState({
        status: "error",
        title: "Sessao ausente",
        message: "Informe uma sessao valida para consultar a conta autenticada.",
        retryable: false
      });
      return;
    }

    setState({ status: "loading" });

    try {
      const capabilities = await getBillingCapabilities(apiClient);
      setState({ status: "success", capabilities });
    } catch (error) {
      setState(toDiagnosticError(error));
    }
  }

  return (
    <section className="capdiag">
      <div className="capdiag__header">
        <p className="capdiag__eyebrow">Diagnostico autenticado</p>
        <h1>Recursos efetivos</h1>
        <p>
          Consulta direta aos recursos comerciais da conta autenticada.
        </p>
      </div>

      <form className="capdiag__form" onSubmit={handleSubmit}>
        <label htmlFor="capabilities-token">Sessao</label>
        <div className="capdiag__controls">
          <input
            id="capabilities-token"
            name="token"
            type="password"
            autoComplete="off"
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
          <button type="submit" disabled={state.status === "loading"}>
            {state.status === "loading" ? "Consultando" : "Consultar"}
          </button>
        </div>
      </form>

      <DiagnosticResult state={state} />
    </section>
  );
}

function DiagnosticResult({ state }: { state: DiagnosticState }) {
  if (state.status === "idle") {
    return (
      <div className="capdiag__panel capdiag__panel--muted">
        <strong>Aguardando consulta</strong>
        <span>O resultado aparecera aqui depois da primeira chamada autenticada.</span>
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <div className="capdiag__panel capdiag__panel--muted" aria-live="polite">
        <strong>Consultando recursos</strong>
        <span>A chamada esta em andamento.</span>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="capdiag__panel capdiag__panel--error" role="alert">
        <strong>{state.title}</strong>
        <span>{state.message}</span>
        {state.retryable && <small>Tente novamente em instantes.</small>}
      </div>
    );
  }

  return (
    <div className="capdiag__grid" aria-live="polite">
      <CapabilityCard title="Conta" rows={[["identificador", state.capabilities.tenant_id]]} />
      <CapabilityCard title="Assinatura" rows={objectRows(state.capabilities.billing)} />
      <CapabilityCard title="Limites" rows={objectRows(state.capabilities.limits)} emptyLabel="Sem limites ativos" />
      <CapabilityCard title="Recursos" rows={objectRows(state.capabilities.features)} emptyLabel="Sem recursos ativos" />
    </div>
  );
}

function CapabilityCard({ title, rows, emptyLabel = "Sem dados" }: { title: string; rows: string[][]; emptyLabel?: string }) {
  return (
    <article className="capdiag__card">
      <h2>{title}</h2>
      {rows.length === 0 ? (
        <p>{emptyLabel}</p>
      ) : (
        <dl>
          {rows.map(([key, value]) => (
            <div key={key}>
              <dt>{key}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  );
}

function objectRows(value: Record<string, unknown>): string[][] {
  return Object.entries(value)
    .filter(([, entry]) => entry !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, entry]) => [key, String(entry)]);
}

function toDiagnosticError(error: unknown): DiagnosticState {
  if (isApiError(error)) {
    if (error.code === "auth_required") {
      return {
        status: "error",
        title: "Sessao invalida",
        message: "A sessao informada nao autorizou a consulta.",
        retryable: false
      };
    }

    if (error.code === "invalid_response") {
      return {
        status: "error",
        title: "Resposta inesperada",
        message: "O contrato retornado nao corresponde ao formato esperado.",
        retryable: false
      };
    }

    return {
      status: "error",
      title: "Falha na consulta",
        message: "Nao foi possivel carregar os recursos agora.",
      retryable: error.retryable
    };
  }

  return {
    status: "error",
    title: "Falha inesperada",
    message: "Nao foi possivel concluir a consulta.",
    retryable: true
  };
}

function isApiError(error: unknown): error is { code: string; retryable: boolean } {
  return typeof error === "object"
    && error !== null
    && "code" in error
    && "retryable" in error;
}
